import React, { useState, useMemo } from 'react';
import { ArrowRight, TrendingDown, PiggyBank } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { PublicSection, Reveal } from '@/components/public';

const MIN = 1000;
const MAX = 50000;
const STEP = 500;
const HC_FEE = 149;

const fmt = (n) => `$${Math.round(n).toLocaleString('en-CA')}`;

/**
 * RoiCalculatorSection — interactive slider that contrasts the "Big 3"
 * 30% commission against Hungry Canadian's flat monthly fee, surfacing the
 * merchant's monthly margin saved in real time.
 */
export default function RoiCalculatorSection() {
  const [sales, setSales] = useState(10000);

  const { big3Take, saved } = useMemo(() => {
    const take = sales * 0.3;
    return { big3Take: take, saved: Math.max(0, take - HC_FEE) };
  }, [sales]);

  return (
    <PublicSection
      id="roi-calculator"
      testId="roi-calculator"
      variant="default"
      align="center"
      eyebrow="The real cost of the big apps"
      title="The average Canadian independent loses $3,500 every month to delivery apps."
      description="Move the slider to your monthly delivery sales and see exactly how much margin you keep with a flat-rate platform."
    >
      <Reveal>
        <div className="max-w-3xl mx-auto bg-card border border-border p-6 sm:p-8 md:p-10 shadow-[0_0_44px_-16px_hsl(var(--primary)/0.35)]">
          {/* Slider */}
          <div className="mb-8 md:mb-10">
            <div className="flex items-end justify-between mb-4">
              <label
                htmlFor="roi-sales-slider"
                className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
              >
                Average monthly delivery sales
              </label>
              <span
                data-testid="roi-sales-value"
                className="font-heading font-black text-2xl md:text-3xl tracking-tight text-foreground leading-none"
              >
                {fmt(sales)}
              </span>
            </div>
            <Slider
              id="roi-sales-slider"
              data-testid="roi-sales-slider"
              min={MIN}
              max={MAX}
              step={STEP}
              value={[sales]}
              onValueChange={(v) => setSales(v[0])}
              aria-label="Average monthly delivery sales"
            />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{fmt(MIN)}</span>
              <span>{fmt(MAX)}</span>
            </div>
          </div>

          {/* Math breakdown */}
          <div className="grid gap-3 sm:grid-cols-2 mb-5">
            <div className="flex items-center justify-between gap-3 bg-background border border-border p-4">
              <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <TrendingDown className="w-4 h-4 text-foreground/50" />
                The Big 3 Take (30%)
              </span>
              <span
                data-testid="roi-big3-take"
                className="font-heading font-bold text-lg text-foreground tabular-nums"
              >
                -{fmt(big3Take)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 bg-background border border-border p-4">
              <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <PiggyBank className="w-4 h-4 text-foreground/50" />
                Hungry Canadian Takes
              </span>
              <span
                data-testid="roi-hc-take"
                className="font-heading font-bold text-lg text-foreground tabular-nums"
              >
                -{fmt(HC_FEE)}
              </span>
            </div>
          </div>

          {/* Highlighted saved box */}
          <div
            data-testid="roi-saved-box"
            className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-primary text-primary-foreground p-6 md:p-7"
          >
            <span className="text-base md:text-lg font-semibold tracking-tight text-center sm:text-left">
              Your Monthly Margin Saved
            </span>
            <span
              data-testid="roi-saved-value"
              className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-none tabular-nums"
            >
              +{fmt(saved)}
            </span>
          </div>

          <p className="mt-5 text-center text-xs text-muted-foreground">
            Based on a flat 30% marketplace commission vs. the Core Starter flat monthly fee.
            Standard payment processing fees apply on both.
          </p>

          <div className="mt-7 text-center">
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
              }}
              data-testid="roi-see-packages-btn"
              className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-foreground text-background font-semibold transition-[transform,opacity] duration-200 hover:-translate-y-0.5 hover:opacity-90"
            >
              Lock in your flat rate <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </Reveal>
    </PublicSection>
  );
}
